import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { getChecklists } from "../redux/actions/checklistActions";

const ITEM_TYPES = {
  CHECKBOX: "checkbox",
  MULTIPLE_CHOICE: "multipleChoice",
  THREE_OPTION: "threeOption",
  TEXT: "text",
  NUMERIC: "numeric",
  IMAGE: "image",
};

const ChecklistScreen = () => {
  const checklist = useSelector((state) => state.checklist.checklist);
  const [responses, setResponses] = useState({});
  const toast = useToast();
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getChecklists());
  }, [dispatch]);

  const handleCheckboxChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleMultipleChoiceChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleTextChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleNumericChange = (itemId, value) => {
    setResponses({ ...responses, [itemId]: value });
  };

  const handleImageUpload = (itemId, files) => {
    const imageFiles = Array.from(files);
    setResponses({
      ...responses,
      [itemId]: imageFiles,
    });
  };

  const handleSubmit = async () => {
    try {
      // TODO: Replace with actual API call to save responses
      console.log("Checklist responses:", responses);
      toast({
        title: "Success",
        description: "Checklist submitted successfully",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit checklist",
        status: "error",
        duration: 3000,
      });
    }
  };

  if (!checklist) {
    return <Box p={8}>No checklist loaded</Box>;
  }

  return (
    <Box p={8} maxW='800px' mx='auto'>
      <Heading mb={6}>{checklist.checklistName}</Heading>

      <VStack spacing={6} align='stretch'>
        {checklist.items?.map((item, index) => (
          <FormControl key={index} as='fieldset'>
            <FormLabel fontSize='lg' fontWeight='bold'>
              {item.question}
            </FormLabel>

            {item.type === ITEM_TYPES.CHECKBOX && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleCheckboxChange(index, value)}>
                <HStack align='start' pl={4} spacing={4}>
                  <Radio value='Yes'>Yes</Radio>
                  <Radio value='No'>No</Radio>
                </HStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.THREE_OPTION && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleMultipleChoiceChange(index, value)}>
                <HStack align='start' pl={4} spacing={4}>
                  <Radio value='Yes'>Yes</Radio>
                  <Radio value='No'>No</Radio>
                  <Radio value='Not Applicable'>Not Applicable</Radio>
                </HStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.MULTIPLE_CHOICE && (
              <RadioGroup value={responses[index] || ""} onChange={(value) => handleMultipleChoiceChange(index, value)}>
                <VStack align='start' pl={4} spacing={2}>
                  {item.options?.map((option, optIndex) => (
                    <Radio key={optIndex} value={option}>
                      {option}
                    </Radio>
                  ))}
                </VStack>
              </RadioGroup>
            )}

            {item.type === ITEM_TYPES.TEXT && (
              <Textarea
                value={responses[index] || ""}
                onChange={(e) => handleTextChange(index, e.target.value)}
                placeholder='Enter your response'
                mt={2}
              />
            )}

            {item.type === ITEM_TYPES.NUMERIC && (
              <Input
                type='number'
                value={responses[index] || ""}
                onChange={(e) => handleNumericChange(index, e.target.value)}
                placeholder='Enter a number'
                mt={2}
              />
            )}

            {item.type === ITEM_TYPES.IMAGE && (
              <VStack align='stretch' spacing={3} mt={2}>
                <Input
                  pt='1'
                  type='file'
                  accept='image/*'
                  multiple
                  onChange={(e) => handleImageUpload(index, e.target.files)}
                  placeholder='Upload images'
                />
                {responses[index] && responses[index].length > 0 && (
                  <VStack align='stretch' spacing={2} mt={2}>
                    {Array.from(responses[index]).map((file, fileIndex) => (
                      <Box
                        key={fileIndex}
                        p={2}
                        border='1px solid'
                        borderColor='gray.200'
                        borderRadius='md'
                        display='flex'
                        alignItems='center'
                        gap={3}
                      >
                        <Image
                          src={URL.createObjectURL(file)}
                          alt={file.name}
                          boxSize='60px'
                          objectFit='cover'
                          borderRadius='md'
                          mr={2}
                        />
                        <Box>
                          <Text fontSize='sm' fontWeight='500'>
                            {file.name}
                          </Text>
                          <Text fontSize='xs' color='gray.500'>
                            {(file.size / 1024).toFixed(2)} KB
                          </Text>
                        </Box>
                      </Box>
                    ))}
                  </VStack>
                )}
              </VStack>
            )}
          </FormControl>
        ))}
      </VStack>

      <HStack justify='flex-end' mt={8}>
        <Button colorScheme='green' size='lg' onClick={handleSubmit}>
          Submit Checklist
        </Button>
      </HStack>
    </Box>
  );
};

export default ChecklistScreen;
